// QuadraticTwoSidesAllCasesTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";
import { formatApproximateMixedFractionLatex as formatMixedFractionLatex } from "../../../../utils/fractionLatex";

/* ===== helpers ===== */
const SIGN_KINDS = ["lt", "gt", "le", "ge"]; // < > ≤ ≥
const SIGN_LATEX = { lt: "<", gt: ">", le: "\\le", ge: "\\ge" };

function quadraticLatex(a, b, c) {
  const ax = a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`;
  const bx =
    b === 0 ? "" : b === 1 ? "+ x" : b === -1 ? "- x" : b > 0 ? `+ ${b}x` : `- ${Math.abs(b)}x`;
  const cx = c === 0 ? "" : c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`;
  return `${ax} ${bx} ${cx}`.replace(/\s+/g, " ").trim();
}
function linearLatex(p, q) {
  const px =
    p === 0 ? "" : p === 1 ? "x" : p === -1 ? "-x" : p > 0 ? `${p}x` : `-${Math.abs(p)}x`;
  const qq = q === 0 ? "" : q > 0 ? `+ ${q}` : `- ${Math.abs(q)}`;
  const s = `${px} ${qq}`.replace(/\s+/g, " ").trim();
  return s.length ? s : "0";
}

/* ===== generator =====
   (ax^2 + bx + c) [znak] (px + q), bez narzuceń na Δ — może wyjść ujemna/zero/dodatnia.
*/
/* ===== generator =====
   (ax^2 + bx + c) [znak] (px + q)
   Teraz z kontrolą częstości przypadków: Δ>0, Δ=0, Δ<0 w proporcji 1/3 : 1/3 : 1/3.
*/
export function generateTask(forceKind) {
  const randi = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const discCase = ["gt0", "eq0", "lt0"][randi(0, 2)];
  const kind = forceKind ?? SIGN_KINDS[randi(0, SIGN_KINDS.length - 1)];

  const splitStandardForm = (B, C) => {
    const p = randi(-6, 6);
    const q = randi(-10, 10);
    return { b: B + p, c: C + q, p, q };
  };

  if (discCase === "gt0") {
    let a = 0;
    while (a === 0 || Math.abs(a) === 1) a = randi(-5, 5);

    let r1, r2, B, C;
    do {
      r1 = randi(-6, 5);
      r2 = randi(r1 + 1, 6);
      B = -a * (r1 + r2);
      C = a * r1 * r2;
    } while (Math.abs(B) > 24 || Math.abs(C) > 60);

    const disc = B * B - 4 * a * C;
    const sqrtDisc = Math.trunc(Math.sqrt(disc));
    const { b, c, p, q } = splitStandardForm(B, C);

    const prettyLatex = `${quadraticLatex(a, b, c)}\\;${SIGN_LATEX[kind]}\\;${linearLatex(p, q)}`;
    const standardLatex = `${quadraticLatex(a, B, C)}\\;${SIGN_LATEX[kind]}\\;0`;

    return {
      type: "QuadraticTwoSidesAllCasesTask",
      data: { a, b, c, p, q, B, C, disc, sqrtDisc, r1, r2, kind, prettyLatex, standardLatex },
    };
  }

  if (discCase === "eq0") {
    let a = 0;
    while (a === 0 || Math.abs(a) === 1) a = randi(-5, 5);

    let r, B, C;
    do {
      r = randi(-5, 5);
      B = -2 * a * r;
      C = a * r * r;
    } while (Math.abs(B) > 24 || Math.abs(C) > 60);
    const disc = 0;
    const sqrtDisc = 0;
    const r1 = r;
    const r2 = r;
    const { b, c, p, q } = splitStandardForm(B, C);

    const prettyLatex = `${quadraticLatex(a, b, c)}\\;${SIGN_LATEX[kind]}\\;${linearLatex(p, q)}`;
    const standardLatex = `${quadraticLatex(a, B, C)}\\;${SIGN_LATEX[kind]}\\;0`;

    return {
      type: "QuadraticTwoSidesAllCasesTask",
      data: { a, b, c, p, q, B, C, disc, sqrtDisc, r1, r2, kind, prettyLatex, standardLatex },
    };
  }

  while (true) {
    let a = 0;
    while (a === 0 || Math.abs(a) === 1) a = randi(-5, 5);

    const b = randi(-8, 8);
    const c = randi(-10, 10);
    const p = randi(-6, 6);
    const q = randi(-10, 10);

    const B = b - p;
    const C = c - q;
    const disc = B * B - 4 * a * C;
    if (disc >= 0) continue;
    const sqrtDisc = null;

    const r1 = null;
    const r2 = null;
    const prettyLatex = `${quadraticLatex(a, b, c)}\\;${SIGN_LATEX[kind]}\\;${linearLatex(p, q)}`;
    const standardLatex = `${quadraticLatex(a, B, C)}\\;${SIGN_LATEX[kind]}\\;0`;

    return {
      type: "QuadraticTwoSidesAllCasesTask",
      data: { a, b, c, p, q, B, C, disc, sqrtDisc, r1, r2, kind, prettyLatex, standardLatex },
    };
  }
}

/* ===== UI ===== */
export function renderTask({ data }) {
  return (
    <p>
      Rozwiąż nierówność: <InlineMath math={data.prettyLatex} />
    </p>
  );
}

export function renderInput(_, userInput = {}, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={'x\\;\\in'} />
        </div>
        <IntervalInput
          value={userInput?.S || ""}
          onChange={(val) => onChange({ ...userInput, S: val })}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-∞, 1) ∪ (4, ∞) • (1,4) • {3} • ℝ • ∅"
        />
      </div>
    </div>
  );
}

export function renderUserAnswer(userInput = {}) {
  const raw =
    typeof userInput === "string"
      ? userInput
      : userInput?.S?.raw || userInput?.S || "";
  const latex = intervalToLatex(raw, { withX: true });
  return raw ? <InlineMath math={latex} /> : <i>(brak odpowiedzi)</i>;
}

/* ===== logika rozwiązania =====
   Zwracamy jedną z postaci:
   - kind: 'outside' / 'between' (jak przy Δ>0),
   - kind: 'point' z 'x0',
   - kind: 'all',
   - kind: 'empty'.
*/
function solutionFor({ a, r1, r2, kind, disc }) {
  const inclusive = kind === "le" || kind === "ge";

  if (disc < 0) {
    // Brak miejsc zerowych: znak całego kwadratu zależy od 'a'.
    // Dla a>0: f(x)>0 wszędzie; dla a<0: f(x)<0 wszędzie.
    const alwaysPositive = a > 0;
    if (kind === "gt")  return alwaysPositive ? { kind: "all" } : { kind: "empty" };
    if (kind === "ge")  return alwaysPositive ? { kind: "all" } : { kind: "empty" };
    if (kind === "lt")  return alwaysPositive ? { kind: "empty" } : { kind: "all" };
    /* kind === "le" */ return alwaysPositive ? { kind: "empty" } : { kind: "all" };
  }

  if (disc === 0) {
    // Jedno miejsce zerowe r = r1 = r2 (parabola styczna do OX)
    const x0 = r1; // oba równe
    if (a > 0) {
      // "do góry": f(x) ≥ 0 dla wszystkich, f(x) ≤ 0 tylko w punkcie styczności
      if (kind === "gt") return { kind: "outside", inclusive: false, left: {R:x0}, right: {L:x0} }; // R\{x0}
      if (kind === "ge") return { kind: "all" };
      if (kind === "lt") return { kind: "empty" };
      if (kind === "le") return { kind: "point", x0 };
    } else {
      // "w dół": f(x) ≤ 0 dla wszystkich, f(x) ≥ 0 tylko w punkcie styczności
      if (kind === "gt") return { kind: "empty" };
      if (kind === "ge") return { kind: "point", x0 };
      if (kind === "lt") return { kind: "outside", inclusive: false, left: {R:x0}, right: {L:x0} }; // R\{x0}
      if (kind === "le") return { kind: "all" };
    }
  }

  // disc > 0: dwa różne miejsca zerowe
  const needOutside = (kind === "gt" || kind === "ge") ? a > 0 : a < 0;
  if (needOutside) {
    return {
      kind: "outside",
      inclusive,
      left:  { L: -Infinity, R: r1, Rclosed: inclusive },
      right: { L: r2,        R: Infinity, Lclosed: inclusive },
    };
  }
  return {
    kind: "between",
    inclusive,
    seg: { L: r1, R: r2, Lclosed: inclusive, Rclosed: inclusive },
  };
}

function intervalsEqualFlexible(expected, parsed) {
  if (!parsed) return false;

  // Akceptuj: ℝ/ALL, ∅/EMPTY, singleton jako {a} lub [a,a],
  // oraz standardowe zbiory przedziałowe.
  if (expected.kind === "all") {
    return parsed.kind === "all" ||
           (parsed.kind === "intervals" &&
            parsed.intervals.length === 1 &&
            parsed.intervals[0].left === -Infinity &&
            parsed.intervals[0].right === Infinity);
  }
  if (expected.kind === "empty") {
    return parsed.kind === "empty" ||
           (parsed.kind === "intervals" && parsed.intervals.length === 0);
  }
  if (expected.kind === "point") {
    if (parsed.kind === "point") {
      return Math.abs(parsed.value - expected.x0) <= 1e-9;
    }
    if (parsed.kind === "intervals" && parsed.intervals.length === 1) {
      const I = parsed.intervals[0];
      return I.leftClosed && I.rightClosed &&
             Math.abs(I.left - expected.x0) <= 1e-9 &&
             Math.abs(I.right - expected.x0) <= 1e-9;
    }
    return false;
  }

  // standardowe porównanie przedziałów (jak w Twoich plikach)
  if (parsed.kind !== "intervals") return false;
  const eq = (x, y, tol = 1e-9) => Math.abs(x - y) <= tol;
  const norm = (I) => ({ L: I.left, R: I.right, Lc: !!I.leftClosed, Rc: !!I.rightClosed });

  const expectList = [];
  if (expected.kind === "outside") {
    expectList.push({ L: -Infinity, R: expected.left.R,  Lc:false, Rc:!!expected.left.Rclosed });
    expectList.push({ L: expected.right.L, R: Infinity, Lc:!!expected.right.Lclosed, Rc:false });
  } else if (expected.kind === "between") {
    expectList.push({
      L: expected.seg.L, R: expected.seg.R,
      Lc: !!expected.seg.Lclosed, Rc: !!expected.seg.Rclosed
    });
  } else {
    return false;
  }

  const got = parsed.intervals.map(norm);
  if (expectList.length !== got.length) return false;

  const used = new Array(got.length).fill(false);
  for (const E of expectList) {
    let ok = false;
    for (let i = 0; i < got.length; i++) {
      if (used[i]) continue;
      const G = got[i];
      const Lok = (E.L === -Infinity ? G.L === -Infinity : eq(E.L, G.L));
      const Rok = (E.R ===  Infinity ? G.R ===  Infinity : eq(E.R, G.R));
      if (Lok && Rok && E.Lc === G.Lc && E.Rc === G.Rc) { used[i] = true; ok = true; break; }
    }
    if (!ok) return false;
  }
  return true;
}

export function checkAnswer({ data }, userInput = {}) {
  const raw =
    typeof userInput?.S === "string" ? userInput.S : userInput?.S?.raw || "";
  if (!raw) return { correct: false };
  const parsed = parseIntervalInput(raw);
  const ans = solutionFor({
    a: data.a, r1: data.r1, r2: data.r2, kind: data.kind, disc: data.disc
  });
  return { correct: intervalsEqualFlexible(ans, parsed) };
}

/* ===== poprawna odpowiedź ===== */
export function renderAnswer({ data }) {
  const ans = solutionFor(data);
  const { r1, r2, disc } = data;

  if (ans.kind === "all") return <InlineMath math={'x\\in \\mathbb{R}'} />;
  if (ans.kind === "empty") return <InlineMath math={'x\\in \\varnothing'} />;
  if (ans.kind === "point") {
    const X0 = formatMixedFractionLatex(ans.x0 ?? r1);
    // pozwólmy też na zapis [x0, x0], ale pokażemy w ładnej postaci singletonu
    return <InlineMath math={`x\\in \\{${X0}\\}`} />;
  }

  // Δ>0 przypadki
  const R1 = formatMixedFractionLatex(r1);
  const R2 = formatMixedFractionLatex(r2);
  const rb = ans.inclusive ? "]" : ")";
  const lb = ans.inclusive ? "[" : "(";

  if (ans.kind === "outside") {
    return <InlineMath math={`x\\in (-\\infty,\\; ${R1}${rb}\\;\\cup\\; ${lb}${R2},\\; \\infty)`} />;
  }
  return <InlineMath math={`x\\in ${lb}${R1},\\; ${R2}${rb}`} />;
}

/* ===== wyjaśnienie =====
   Krok 1: przeniesienie na jedną stronę.
   Krok 2: Δ i (jeśli trzeba) pierwiastki — z pełnym podstawieniem.
   Krok 3: szkic i decyzja: Δ<0 / Δ=0 / Δ>0 + znaki.
   Krok 4: zapis odpowiedzi; oś liczbowa (interwały lub punkt).
*/
export function renderExplanation({ data }) {
  const { a, B, C, disc, sqrtDisc, r1, r2, kind, prettyLatex, standardLatex } = data;

  const aPos = a > 0;
  const armsTxt = aPos ? "ramiona do góry" : "ramiona w dół";

  const ans = solutionFor({ a, r1, r2, kind, disc });

  // Δ i pierwiastki — ładne podstawienia
  const num = (n) => (n < 0 ? `(${n})` : `${n}`);
  const discLatex = String.raw`\Delta = ${num(B)}^2 - 4\cdot ${num(a)}\cdot ${num(C)} = ${disc}`;

  const R1_ltx = r1 != null ? formatMixedFractionLatex(r1) : "";
  const R2_ltx = r2 != null ? formatMixedFractionLatex(r2) : "";

  const minusBLatex = B < 0 ? `-(${B})` : `-${B}`;
  const denom = 2 * a;
  const denomFormulaLatex = `2\\cdot ${num(a)}`;
  const rootVariants = disc > 0
    ? [
        { sign: "-", numerator: -B - sqrtDisc, value: (-B - sqrtDisc) / denom },
        { sign: "+", numerator: -B + sqrtDisc, value: (-B + sqrtDisc) / denom },
      ].sort((left, right) => left.value - right.value)
    : [];

  const rootLine = (label, root, resultLatex) => String.raw`
\begin{aligned}
${label} &= \dfrac{${minusBLatex} ${root.sign} ${sqrtDisc}}{${denomFormulaLatex}} \\[4pt]
${label} &= \dfrac{${root.numerator}}{${denom}} \\[4pt]
${label} &= ${resultLatex}
\end{aligned}
`;

  const tangentRootLine = String.raw`
\begin{aligned}
x_0 &= \dfrac{${minusBLatex}}{${denomFormulaLatex}} \\[4pt]
x_0 &= \dfrac{${-B}}{${denom}} \\[4pt]
x_0 &= ${R1_ltx}
\end{aligned}
`;

  // zapis odpowiedzi
  let solLatex = "";
  if (ans.kind === "all") solLatex = String.raw`x\in \mathbb{R}`;
  else if (ans.kind === "empty") solLatex = String.raw`x\in \varnothing`;
  else if (ans.kind === "point") solLatex = String.raw`x\in \{${formatMixedFractionLatex(ans.x0 ?? r1)}\}`;
  else if (ans.kind === "outside") {
    solLatex = String.raw`x\in (-\infty,\; ${R1_ltx}${ans.inclusive ? "]" : ")"}\ \cup\ ${ans.inclusive ? "[" : "("}${R2_ltx},\; \infty)`;
  } else {
    solLatex = String.raw`x\in ${ans.inclusive ? "[" : "("}${R1_ltx},\; ${R2_ltx}${ans.inclusive ? "]" : ")"}`;
  }

  const chooseTxt =
    kind === "gt" || kind === "ge"
      ? "bierzemy część wykresu powyżej osi OX"
      : "bierzemy część wykresu poniżej osi OX";

  // wybór trybu korzeni dla ParabolaShape
  const mode =
    disc < 0 ? "none" :
    disc === 0 ? "one" : "two";

  const leftLabelTex  = disc > 0 ? R1_ltx : (disc === 0 ? R1_ltx : undefined);
  const rightLabelTex = disc > 0 ? R2_ltx : (disc === 0 ? R1_ltx : undefined);

  return (
    <div className="space-y-3">
      {/* KROK 1 */}
      <p><strong>Krok 1:</strong> Przenosimy wszystko na jedną stronę.</p>
      <BlockMath math={String.raw`${prettyLatex}\quad \Longleftrightarrow \quad ${standardLatex}`} />

      {/* KROK 2 */}
      <p><strong>Krok 2:</strong> Liczymy deltę i miejsca zerowe, jeśli istnieją.</p>
      <BlockMath math={discLatex} />
      {disc > 0 && (
        <>
          <BlockMath math={String.raw`\sqrt{\Delta} = ${sqrtDisc}`} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1rem",
              alignItems: "start",
            }}
          >
            <div>
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                <InlineMath math={'x_1'} />
              </div>
              <BlockMath math={rootLine("x_1", rootVariants[0], R1_ltx)} />
            </div>
            <div>
              <div style={{ textAlign: "center", fontWeight: 600 }}>
                <InlineMath math={'x_2'} />
              </div>
              <BlockMath math={rootLine("x_2", rootVariants[1], R2_ltx)} />
            </div>
          </div>
        </>
      )}
      {disc === 0 && (
        <>
          <BlockMath math={String.raw`\sqrt{\Delta} = 0`} />
          <BlockMath math={tangentRootLine} />
        </>
      )}
      {disc < 0 && (
        <p style={{ textAlign: "center" }}>
          Brak miejsc zerowych — wykres nie przecina osi OX.
        </p>
      )}

      {/* KROK 3 */}
      <p>
        <strong>Krok 3:</strong> Rysujemy uproszczony wykres (
        <InlineMath math={`a=${a}`} /> — {aPos ? "dodatni" : "ujemny"}{" "}
        <InlineMath math={aPos ? 'a>0' : 'a<0'} /> → {armsTxt}
        ) i sprawdzamy, gdzie spełniona jest nierówność — {chooseTxt}.
      </p>

      <div style={{ maxWidth: 620, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={aPos ? 1 : -1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks={disc >= 0}
          curveColor="#f59e0b"
          rootMode={mode}
          tangentX={0}
          labelLeftTex={leftLabelTex}
          labelRightTex={rightLabelTex}
          highlightMode={ans.kind === "outside" ? "outside" : (ans.kind === "between" ? "between" : undefined)}
          highlightLeftClosed={ans.inclusive}
          highlightRightClosed={ans.inclusive}
          highlightColor="#a78bfa"
        />
      </div>

      {disc === 0 && (
        <p style={{ textAlign: "center" }}>
          Parabola styka się z osią OX w punkcie{" "}
          <InlineMath math={R1_ltx} />
          {" "}i leży {aPos ? "nad" : "pod"} osią poza tym punktem.
        </p>
      )}
      {disc < 0 && (
        <p style={{ textAlign: "center" }}>
          Parabola leży cała {aPos ? "nad" : "pod"} osią OX.
        </p>
      )}

      {ans.kind !== "point" && (
        <>
          {/* KROK 4 */}
          <p><strong>Krok 4:</strong> Zapisujemy odpowiedź{ans.kind === "all" || ans.kind === "empty" ? "" : " i zaznaczamy ją na osi liczbowej"}.</p>
          <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
            <InlineMath math={solLatex} />
          </div>
        </>
      )}

      {/* Oś liczbową rysujemy tylko dla przedziałów i punktów */}
      {(ans.kind === "between" || ans.kind === "outside") && (
        <div style={{ maxWidth: 620, margin: "0 auto" }}>
          {ans.kind === "outside" ? (
            <NumberLineRanges
              lockPair
              showZero
              ranges={[
                { to: r1, rightClosed: !!ans.inclusive, rightLabelLatex: R1_ltx },
                { from: r2, leftClosed: !!ans.inclusive, leftLabelLatex: R2_ltx },
              ]}
            />
          ) : (
            <NumberLineRanges
              lockPair
              showZero
              ranges={[
                {
                  from: r1, to: r2,
                  leftClosed: !!ans.inclusive, rightClosed: !!ans.inclusive,
                  leftLabelLatex: R1_ltx, rightLabelLatex: R2_ltx,
                },
              ]}
            />
          )}
        </div>
      )}

    </div>
  );
}

